import { useEffect, useState, useCallback } from 'react';
import { useDebounce } from 'use-debounce';

export interface FormData {
    email: string;
    isTrial: boolean;
    isAdmin: boolean;
}

type UseUserFormProps = {
    initialFormData: FormData;
    debounceDelay?: number;
};

export function useUserForm({ initialFormData, debounceDelay = 300 }: UseUserFormProps) {
    // Form data state from the api call
    const [formData, setFormData] = useState<FormData>(initialFormData);

    // Raw form debouncing and data or the dirty form data once the user
    // filled out information in textfields
    const [rawFormData, setRawFormData] = useState<FormData>(initialFormData);
    const [debouncedFormData] = useDebounce(rawFormData, debounceDelay);

    // Touch fields recording to handle error textfield validation
    const [touchedFields, setTouchedFields] = useState<Record<keyof FormData, boolean>>({
        email: false,
        isTrial: false,
        isAdmin: false,
    });

    // Debouncing mechanism for form state (create / edit)
    useEffect(() => {
        setFormData(debouncedFormData);
    }, [debouncedFormData]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value, type, checked } = e.target as HTMLInputElement;
            setRawFormData((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        },
        [],
    );

    const handleBlur = useCallback(
        (field: keyof FormData) => {
            setTouchedFields((prev) => ({ ...prev, [field]: true }));
        },
        [],
    );

    const resetForm = useCallback((newInitialData?: FormData) => {
        const resetData = newInitialData || initialFormData;
        setRawFormData(resetData);
        setFormData(resetData);
        setTouchedFields({
            email: false,
            isTrial: false,
            isAdmin: false,
        });
    }, [initialFormData]);

    const isFormValid = useCallback(() => {
        return (
            !!formData.email &&
            formData.isTrial !== undefined &&
            formData.isAdmin !== undefined
        );
    }, [formData]);

    return {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid,
    };
}
